/**
 * The tabbed side panel and the strip of icons that switches it.
 *
 * One panel holds everything that is a list of the player's own things, the way a
 * modern client does it: inventory, skills, friends, quests, settings. Equipment
 * is the exception and gets a panel of its own, because comparing what is in the
 * bag against what is on the character is the whole point of that screen, and a
 * panel that can sit beside the bag is what makes that possible.
 */

import { SkillIcon } from './SkillIcon';
import { experienceForLevel, MAX_LEVEL, SKILL_NAMES } from '@inverse/common';
import type { IconWire, QuestWire } from '@inverse/protocol';
import { Glyph } from './Glyph';
import { useState, type ReactNode } from 'react';
import { TAB_LABELS, type TabName } from './tabs';

interface TabDefinition {
  name: TabName;
  label: string;
  icon: ReactNode;
}

/**
 * Icons are drawn rather than written, so they read the same on every machine and
 * do not depend on a font being present.
 */
const stroke = {
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.6,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
};

const BagIcon = (
  <svg viewBox="0 0 20 20" aria-hidden="true">
    <path d="M4 7h12l-1 9H5L4 7Z" {...stroke} />
    <path d="M7.5 7V5.5a2.5 2.5 0 0 1 5 0V7" {...stroke} />
  </svg>
);

const SkillsIcon = (
  <svg viewBox="0 0 20 20" aria-hidden="true">
    <path d="M4 16V9M8.6 16V5M13.2 16v-4.5M17 16V7" {...stroke} />
  </svg>
);

const QuestIcon = (
  <svg viewBox="0 0 20 20" aria-hidden="true">
    <path
      d="m10 3.6 2 4.2 4.5.6-3.3 3.2.8 4.6-4-2.2-4 2.2.8-4.6L3.5 8.4 8 7.8l2-4.2Z"
      {...stroke}
    />
  </svg>
);

const FriendsIcon = (
  <svg viewBox="0 0 20 20" aria-hidden="true">
    <circle cx="7.5" cy="7" r="2.6" {...stroke} />
    <path d="M3 16c0-2.5 2-4.2 4.5-4.2S12 13.5 12 16" {...stroke} />
    <path d="M13.2 6.2a2.4 2.4 0 0 1 0 4.4M14 12.2c2 .4 3.3 1.9 3.3 3.8" {...stroke} />
  </svg>
);

const SettingsIcon = (
  <svg viewBox="0 0 20 20" aria-hidden="true">
    <circle cx="10" cy="10" r="2.4" {...stroke} />
    <path
      d="M10 3.2v2M10 14.8v2M3.2 10h2M14.8 10h2M5.2 5.2 6.6 6.6M13.4 13.4l1.4 1.4M14.8 5.2 13.4 6.6M6.6 13.4 5.2 14.8"
      {...stroke}
    />
  </svg>
);

const GearIcon = (
  <svg viewBox="0 0 20 20" aria-hidden="true">
    <path d="M10 3.2 5 5v4c0 3.4 2.1 6.2 5 7.8 2.9-1.6 5-4.4 5-7.8V5l-5-1.8Z" {...stroke} />
  </svg>
);

/** A spark over an open hand, for the spell book. */
const SpellIcon = (
  <svg viewBox="0 0 20 20" aria-hidden="true">
    <path d="M10 3c.6 3 2 4.4 5 5-3 .6-4.4 2-5 5-.6-3-2-4.4-5-5 3-.6 4.4-2 5-5Z" {...stroke} />
    <path d="M15.5 13.5v3M14 15h3" {...stroke} />
  </svg>
);

/** A sun rising, for faith. */
const FaithIcon = (
  <svg viewBox="0 0 20 20" aria-hidden="true">
    <path d="M4 14h12M6.5 14a3.5 3.5 0 0 1 7 0" {...stroke} />
    <path d="M10 5v2.2M5 7.5l1.4 1.4M15 7.5l-1.4 1.4" {...stroke} />
  </svg>
);

const SwordIcon = (
  <svg viewBox="0 0 20 20" aria-hidden="true">
    <path d="M14.5 3.5 8 10l1.9 1.9 6.6-6.5V3.5h-2Z" {...stroke} />
    <path d="M7 11.2 4.2 14a2 2 0 0 0 2.8 2.8l2.8-2.8" {...stroke} />
  </svg>
);

const TABS: readonly TabDefinition[] = [
  { name: 'inventory', label: TAB_LABELS.inventory, icon: BagIcon },
  { name: 'combat', label: TAB_LABELS.combat, icon: SwordIcon },
  { name: 'skills', label: TAB_LABELS.skills, icon: SkillsIcon },
  { name: 'quests', label: TAB_LABELS.quests, icon: QuestIcon },
  { name: 'friends', label: TAB_LABELS.friends, icon: FriendsIcon },
  { name: 'settings', label: TAB_LABELS.settings, icon: SettingsIcon },
];

/**
 * The four stances of whichever way somebody is fighting.
 *
 * A blade is swung four ways, a bow is drawn four ways, and a staff casts in
 * four elements. What is in your hands decides which set of four these are, so
 * the panel is told rather than working it out from what it can see.
 */
const WAYS: readonly {
  name: string;
  gives: string;
  note: string;
  /** What it looks like, for the ways of fighting that have a look. */
  icon?: IconWire;
}[][] = [
  [
    { name: 'Accurate', gives: 'Attack', note: 'Lands more often.' },
    { name: 'Aggressive', gives: 'Strength', note: 'Hits harder.' },
    { name: 'Defensive', gives: 'Defence', note: 'Harder to hit back.' },
    { name: 'Controlled', gives: 'Attack, Strength and Defence', note: 'A little of each.' },
  ],
  [
    { name: 'Accurate', gives: 'Ranged', note: 'Lands more often.' },
    { name: 'Overdraw', gives: 'Ranged', note: 'Hits harder.' },
    { name: 'Rapid', gives: 'Ranged', note: 'One tick quicker between shots.' },
    {
      name: 'Longrange',
      gives: 'Ranged and Defence',
      note: 'Two tiles further, and harder to hit back.',
    },
  ],
  [
    {
      name: 'Air',
      gives: 'Magic',
      note: 'Cast in air.',
      icon: { glyph: 'wind', colour: '#d8e6f0' },
    },
    {
      name: 'Water',
      gives: 'Magic',
      note: 'Cast in water.',
      icon: { glyph: 'drop', colour: '#4fa3e0' },
    },
    {
      name: 'Earth',
      gives: 'Magic',
      note: 'Cast in earth.',
      icon: { glyph: 'stone', colour: '#b58b5a' },
    },
    {
      name: 'Fire',
      gives: 'Magic',
      note: 'Cast in fire.',
      icon: { glyph: 'flame', colour: '#ff8a3d' },
    },
  ],
];

/** The second line under an element, which is the same line for all four. */
const ELEMENT_NOTE =
  'Worth half again against anything weak to it, and next to nothing against anything hardened to it.';

export function Fighting({
  way,
  stance,
  onStance,
  retaliate,
  onRetaliate,
  onHoverText,
}: {
  /** Melee, ranged or magic, from what they are holding. */
  way: number;
  stance: number;
  onStance: (stance: number) => void;
  retaliate: boolean;
  onRetaliate: (on: boolean) => void;
  /** What the pointer is resting on, said in a tooltip rather than on the button. */
  onHoverText?: (held: { title: string; lines: string[] } | null) => void;
}): JSX.Element {
  const casting = way === 2;
  const ways = (WAYS[way] ?? WAYS[0] ?? []).map((held, index) => ({ ...held, stance: index }));

  return (
    <div className="fighting">
      <div className="stances">
        {ways.map((held) => (
          <button
            key={held.stance}
            type="button"
            className={stance === held.stance ? 'stance on' : 'stance'}
            onClick={() => onStance(held.stance)}
            onPointerEnter={() =>
              onHoverText?.({
                title: held.name,
                lines: casting
                  ? [held.note, ELEMENT_NOTE, `Trains ${held.gives}.`]
                  : [held.note, `Trains ${held.gives}.`],
              })
            }
            onPointerLeave={() => onHoverText?.(null)}
          >
            {/* Each element wears its own mark, on the button it belongs to.
                It used to sit in a box of its own beside the four, which said
                what was selected twice and looked like a fifth thing. */}
            {held.icon ? <Glyph icon={held.icon} className="stance-mark" /> : null}
            <span className="stance-name">{held.name}</span>
          </button>
        ))}
      </div>

      <button
        type="button"
        className={retaliate ? 'stance retaliate on' : 'stance retaliate'}
        onClick={() => onRetaliate(!retaliate)}
        onPointerEnter={() =>
          onHoverText?.({
            title: 'Auto Retaliate',
            lines: [
              'On, anything that hits you is answered without being told to.',
              'Off, you fight only what you pick.',
            ],
          })
        }
        onPointerLeave={() => onHoverText?.(null)}
      >
        Auto Retaliate
        <span className={retaliate ? 'pip on' : 'pip'} />
      </button>
    </div>
  );
}

export function TabStrip({
  active,
  open,
  gearOpen,
  onSelect,
  onToggleGear,
  spellsOpen = false,
  faithOpen = false,
  onToggleSpells,
  onToggleFaith,
}: {
  active: TabName | null;
  /** Everything up at once, for when panels float rather than sharing the bar. */
  open?: readonly TabName[];
  gearOpen: boolean;
  /** Choosing the tab already open closes the panel, as a toggle. */
  onSelect: (tab: TabName) => void;
  onToggleGear: () => void;
  /** The spell book and the faith panel, windows of their own like the kit. */
  spellsOpen?: boolean;
  faithOpen?: boolean;
  onToggleSpells?: () => void;
  onToggleFaith?: () => void;
}): JSX.Element {
  const lit = (tab: TabName): boolean => (open ? open.includes(tab) : active === tab);
  return (
    <div className="tabs">
      <button
        type="button"
        className={gearOpen ? 'tab on' : 'tab'}
        title="Equipment"
        aria-pressed={gearOpen}
        onClick={onToggleGear}
      >
        {GearIcon}
      </button>

      {onToggleSpells ? (
        <button
          type="button"
          className={spellsOpen ? 'tab on' : 'tab'}
          title="Spells"
          aria-pressed={spellsOpen}
          onClick={onToggleSpells}
        >
          {SpellIcon}
        </button>
      ) : null}

      {onToggleFaith ? (
        <button
          type="button"
          className={faithOpen ? 'tab on' : 'tab'}
          title="Faith"
          aria-pressed={faithOpen}
          onClick={onToggleFaith}
        >
          {FaithIcon}
        </button>
      ) : null}

      {TABS.map((tab) => (
        <button
          key={tab.name}
          type="button"
          className={lit(tab.name) ? 'tab on' : 'tab'}
          title={tab.label}
          aria-pressed={lit(tab.name)}
          onClick={() => onSelect(tab.name)}
        >
          {tab.icon}
        </button>
      ))}
    </div>
  );
}

/**
 * The journal: what has been started, and what it is asking for now.
 *
 * A quest is a name and a list of steps; what somebody carries is the step they
 * are on. Anything not started is left out, since a list of every quest in the
 * world is a table of contents rather than a journal.
 */
export function Journal({
  quests,
  progress,
  flags,
  levels,
  pinned,
  onPin,
}: {
  quests: readonly QuestWire[];
  progress: readonly { id: string; step: number }[];
  /** What the world has written down about this player, for what to keep quiet. */
  flags: readonly { name: string; value: string }[];
  levels: readonly number[];
  /** Which quest is being followed, so its step is the one marked in the world. */
  pinned: string | null;
  onPin: (quest: string | null) => void;
}): JSX.Element {
  const [open, setOpen] = useState<string | null>(null);

  /**
   * Whether a quest is worth mentioning to this player yet.
   *
   * The conditions come down as written text and are read here, because whether
   * they hold is a question about whoever is holding the journal. Only the ones
   * the journal can answer are asked: a level, a flag, another quest. Anything
   * else is taken as met, since a journal that hides a quest for a reason it
   * cannot check is a journal that hides it forever.
   */
  const shown = (quest: QuestWire): boolean => {
    if (quest.hiddenUntil.length === 0) return true;

    let rules: {
      flag?: string;
      is?: string;
      quest?: string;
      atLeast?: number;
      skill?: string;
      level?: number;
      not?: boolean;
    }[] = [];

    try {
      rules = JSON.parse(quest.hiddenUntil) as typeof rules;
    } catch {
      return true;
    }

    return rules.every((rule) => {
      let holds = true;

      if (rule.flag !== undefined) {
        const held = flags.find((one) => one.name === rule.flag)?.value ?? '';
        holds = rule.is === undefined ? held.length > 0 : held === rule.is;
      }

      if (rule.quest !== undefined) {
        const at = progress.find((one) => one.id === rule.quest)?.step ?? 0;
        holds = holds && at >= (rule.atLeast ?? 1);
      }

      if (rule.skill !== undefined && rule.level !== undefined) {
        const at = SKILL_NAMES.findIndex((name) => name.toLowerCase() === rule.skill);
        holds = holds && (levels[at] ?? 1) >= rule.level;
      }

      return rule.not === true ? !holds : holds;
    });
  };

  const listed = quests.filter(shown);

  /** How far along one quest is, which is what its colour says. */
  const stateOf = (quest: QuestWire): 'not started' | 'started' | 'finished' => {
    const at = progress.find((entry) => entry.id === quest.id)?.step ?? 0;
    if (at <= 0) return 'not started';

    return at > quest.steps.length ? 'finished' : 'started';
  };

  const showing = listed.find((quest) => quest.id === open);

  if (showing) {
    const state = stateOf(showing);
    const at = progress.find((entry) => entry.id === showing.id)?.step ?? 0;
    const step = state === 'started' ? showing.steps[at - 1] : undefined;

    return (
      <div className="panel-body journal">
        <button type="button" className="link" onClick={() => setOpen(null)}>
          Back to the list
        </button>

        <h3 className={`journal-title ${state.replace(' ', '-')}`}>{showing.name}</h3>
        <p>{showing.summary || 'Nothing written about it yet.'}</p>

        {state === 'not started' ? (
          <>
            <h4>How it starts</h4>
            <p className="hint">{showing.hint || 'Nobody has said.'}</p>
          </>
        ) : null}

        {step ? (
          <>
            <h4>What to do now</h4>
            <p>{step.text}</p>
            {step.note ? <p className="hint">{step.note}</p> : null}
          </>
        ) : null}

        {state === 'finished' ? <p className="hint">Finished.</p> : null}

        {/* What it asks of somebody: what was written down, and what its own
            steps ask for whether or not anybody wrote it down. */}
        {showing.requirements.length > 0 || showing.derived.length > 0 ? (
          <>
            <h4>What it asks of you</h4>
            <ul className="journal-needs">
              {showing.requirements.map((line) => (
                <li key={line}>{line}</li>
              ))}
              {showing.derived
                .filter((line) => !showing.requirements.includes(line))
                .map((line) => (
                  <li className="worked-out" key={line}>
                    {line}
                  </li>
                ))}
            </ul>
          </>
        ) : null}

        {/* What has been done, and what is being done. Nothing after that:
            a column of Not yet known told nobody anything and gave away how
            long the quest was, which is half of what a quest keeps back. */}
        {/* Only for a quest that runs in a line. Somebody on step four of a
            quest that forks may never have seen step two, and a journal that
            ticks it off is a journal telling them they did something they did
            not do. What they are doing now is above, and it is the true part. */}
        {showing.steps.length > 0 && at > 0 && !showing.forks ? (
          <>
            <h4>Steps</h4>
            <ol className="journal-steps">
              {showing.steps.slice(0, at).map((one, index) => (
                <li className={at > index + 1 ? 'done' : 'now'} key={`${one.text}-${index}`}>
                  {one.text}
                </li>
              ))}
            </ol>
          </>
        ) : null}

        {showing.forks && at > 0 && state !== 'finished' ? (
          <p className="hint">There is more than one road through this one.</p>
        ) : null}

        {state !== 'not started' ? (
          <button
            type="button"
            className="wide"
            disabled={state === 'finished'}
            onClick={() => onPin(pinned === showing.id ? null : showing.id)}
          >
            {pinned === showing.id ? 'Stop following this one' : 'Follow this one'}
          </button>
        ) : null}
      </div>
    );
  }

  // The list: everything there is, in the colour of how far along it is.
  return (
    <div className="panel-body journal">
      {[...listed]
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((quest) => {
          const state = stateOf(quest);
          const at = progress.find((entry) => entry.id === quest.id)?.step ?? 0;

          return (
            <button
              type="button"
              className={`journal-row ${state.replace(' ', '-')}`}
              key={quest.id}
              onClick={() => setOpen(quest.id)}
            >
              <span className="journal-name">{quest.name}</span>
              <span className="journal-state">
                {state === 'finished'
                  ? 'Finished'
                  : state === 'started'
                    ? `Step ${Math.min(at, quest.steps.length)} of ${quest.steps.length}`
                    : 'Not started'}
              </span>
              {pinned === quest.id ? <span className="journal-following">Following</span> : null}
            </button>
          );
        })}

      {listed.length === 0 ? <p className="hint">Nothing to go after yet.</p> : null}
    </div>
  );
}

/** The player's levels, in the order they are stored. */
export function Skills({
  levels,
  experience,
  combatLevel,
  onHoverText,
  onGuide,
}: {
  levels: readonly number[];
  experience: readonly number[];
  /** What the character fights at, which belongs beside what they are worth overall. */
  combatLevel: number;
  onHoverText?: (held: { title: string; lines: string[] } | null) => void;
  /** Opens the guide for one skill, which is what pressing it is for. */
  onGuide?: (skill: string) => void;
}): JSX.Element {
  const total = levels.reduce((sum, level) => sum + level, 0);

  /**
   * How far through the current level somebody is, from nought to one.
   *
   * Full at the top level: there is nothing left to be part way through, and a
   * bar sitting empty there would read as no progress rather than as finished.
   */
  const through = (skill: number): number => {
    const level = levels[skill] ?? 1;
    if (level >= MAX_LEVEL) return 1;

    const held = Math.floor(experience[skill] ?? 0);
    const start = experienceForLevel(level);
    const next = experienceForLevel(level + 1);

    return Math.min(1, Math.max(0, (held - start) / Math.max(1, next - start)));
  };
  const whole = (value: number): string => Math.floor(value).toLocaleString();

  /** How far through a level somebody is, and how far through the lot of it. */
  const progress = (skill: number): { title: string; lines: string[] } => {
    const held = Math.floor(experience[skill] ?? 0);
    const level = levels[skill] ?? 1;

    const start = experienceForLevel(level);
    const next = experienceForLevel(Math.min(MAX_LEVEL, level + 1));
    const through = Math.max(0, held - start);
    const span = Math.max(1, next - start);

    const top = experienceForLevel(MAX_LEVEL);
    const share = (part: number, all: number): string => `${Math.floor((part / all) * 100)}%`;

    return {
      title: `${SKILL_NAMES[skill] ?? 'Skill'} ${level}`,
      lines: [
        `${whole(through)}/${whole(span)} - (${share(through, span)})`,
        `${whole(held)}/${whole(top)} - (${share(held, top)})`,
      ],
    };
  };

  return (
    <div className="panel-body">
      <div className="skill-grid">
        {SKILL_NAMES.map((name, skill) => (
          <button
            key={name}
            type="button"
            className="skill"
            // The picture is the name here, the way the skills have always been
            // laid out: three to a row, a level beside each. The name and how
            // far along it is come up on hover, and pressing it opens its guide.
            aria-label={`${name}, level ${levels[skill] ?? 1}`}
            data-name={name}
            onPointerEnter={() => onHoverText?.(progress(skill))}
            onPointerLeave={() => onHoverText?.(null)}
            onClick={() => onGuide?.(name.toLowerCase())}
          >
            <SkillIcon skill={name} />
            <span className="skill-level">{levels[skill] ?? 1}</span>

            {/* How far through the level, as a line under it. A number would
                have to be read; a line is seen without looking. */}
            <span className="skill-bar" aria-hidden>
              <span
                className="skill-bar-filled"
                style={{ width: `${Math.round(through(skill) * 100)}%` }}
              />
            </span>
          </button>
        ))}
      </div>

      {/* One line rather than two. Two rows of two words each cost the panel
          enough height to need a scrollbar, for a total and a combat level that
          fit side by side with room to spare. */}
      <div className="skill-total">
        <span>
          Total <b>{total}</b>
        </span>
        <span
          onPointerEnter={() =>
            onHoverText?.({
              title: `Combat level ${combatLevel}`,
              lines: [
                'A quarter of Defence, Constitution and half your Faith.',
                'Plus the best of melee, ranged or magic: Attack and Strength at just over a third, or ranged or magic at half again their level.',
              ],
            })
          }
          onPointerLeave={() => onHoverText?.(null)}
        >
          Combat <b>{combatLevel}</b>
        </span>
      </div>
    </div>
  );
}
